import { Spacer } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import CollapsibleView from "@components/molecules/CollapsibleView";
import CrossPlatformCalendar from "@components/molecules/DynamicForm/components/CrossPlatformCalendar";
import { useEffect, useMemo, useState } from "react";
import styled from "styled-components/native";
import { FlatList } from "react-native";
import RadioButton from "@components/atoms/RadioButton";
import useScreenOptionsHooks from "../../../hooks/useScreenOptionsHooks";
import {
  setSelectedShippingMethod,
  useOrderCartSlice,
} from "@redux/slices/orderCartSlice";
import { useDispatch } from "react-redux";
import { getAvailableShippingMethods } from "./utils";
import { useDynamicFormState } from "@scenes/bulk-request/context/DynamicFormContext";
import currency from "currency.js";
import { useUserCurrency } from "../../../hooks/getCurrency";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { useIsFocused } from "@react-navigation/native";

const ShippingMethod = ({
  orderCart,
  priceMode,
  totalPrice,
}: {
  orderCart?: any;
  priceMode?: number;
  totalPrice?: number;
}) => {
  const { typography, theme, t } = useScreenOptionsHooks();
  const dispatch = useDispatch();
  const isFocused = useIsFocused();
  const { setRequestFormValue } = useDynamicFormState();
  const showCalender =
    orderCart?.shipping_method?.expectedDueDate?.price_modes?.includes(
      priceMode
    );
  const userCurrency = useUserCurrency();
  const userInfo = useUserInfo();
  const { shippingData } = useOrderCartSlice();
  const shippingMethods = useMemo(
    () =>
      getAvailableShippingMethods(shippingData?.shippingMethods, totalPrice),
    [shippingData?.shippingMethods, totalPrice]
  );
  const [date, setDate] = useState<Date | undefined>(undefined);
  const handleItemSelection = (item: any) => {
    dispatch(setSelectedShippingMethod(item));
  };
  const handleDateSelection = (val) => {
    setDate(val);
    setRequestFormValue(t("shippingMethod.date.label.text"), val);
  };

  useEffect(() => {
    if (!isFocused) {
      return;
    }

    const price = Number(totalPrice);
    if (!Number.isFinite(price) || price <= 0) {
      return;
    }

    const selected = shippingData.selectedItem;
    if (!shippingMethods?.length) {
      return;
    }

    const idOf = (method: any) => String(method?.id ?? "");
    const feeOf = (method: any) => Number(method?.flat_fee ?? 0);
    const isSameMethod = (method: any) =>
      idOf(method) === idOf(selected) && feeOf(method) === feeOf(selected);

    const current =
      shippingMethods.find((method) => idOf(method) === idOf(selected)) ??
      shippingMethods[0];

    if (!isSameMethod(current)) {
      dispatch(setSelectedShippingMethod(current));
    }
  }, [
    isFocused,
    totalPrice,
    shippingMethods,
    shippingData.selectedItem,
    dispatch,
  ]);

  return (
    <CollapsibleView
      initExpanded={true}
      title={t("shippingMethod.header.title.text")}
      arrowStyling={{ color: theme["core-04"] }}
      applyShadow
      applyTopPadding={false}
      applyBottomPadding={false}
    >
      <Divider />
      <Container>
        {showCalender && (
          <>
            <CrossPlatformCalendar
              label={t("shippingMethod.date.label.text")}
              value={date}
              onChange={(val) => handleDateSelection(val)}
              minDate={new Date() || undefined}
            />
            <Spacer size={SpaceType.Double} />
          </>
        )}
        <FlatList
          keyExtractor={(item: any) => String(item.id)}
          data={shippingMethods}
          renderItem={({ item }) => (
            <CheckBoxDisplay>
              <RadioButton
                label={`${item?.name} ${currency(Number(item?.flat_fee), {
                  symbol: userCurrency,
                  precision: userInfo.roundUpPrices() ? 0 : 2,
                }).format()}`}
                labelColor={
                  shippingData.selectedItem?.id === item.id
                    ? theme["core-05"]
                    : theme["core-06"]
                }
                checked={shippingData.selectedItem?.id === item.id}
                styles={
                  shippingData.selectedItem?.id === item.id
                    ? typography["Body-M"]
                    : typography["Body-R"]
                }
                onPress={() => handleItemSelection(item)}
                disabled={shippingData.selectedItem?.id === item.id}
              />
            </CheckBoxDisplay>
          )}
        />
      </Container>
    </CollapsibleView>
  );
};

const Container = styled.View`
  padding: 16px;
`;

const Divider = styled.View`
  height: 1px;
  flex-direction: row;
  background-color: ${({ theme }) => theme["core-03"]};
  width: 100%;
  max-width: 100%;
`;

const CheckBoxDisplay = styled.View`
  flex-direction: row;
  align-items: center;
  gap: ${SpaceType.Extra}px;
`;

export default ShippingMethod;
